/**
 * Account Settings Page - YUL ON TAP
 * 
 * User self-service account management:
 * - Password change (triggers session invalidation)
 * - Account information display
 * - Theme preferences
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { authAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import CommandBar from '../components/CommandBar';
import { toast } from 'sonner';
import {
  User,
  Lock,
  Loader2,
  Shield,
  CheckCircle2,
  Moon,
  Sun,
  Palette,
  KeyRound,
} from 'lucide-react';

export default function AccountSettings() {
  const { t, i18n } = useTranslation();
  const { user, logout, currentWorkspace, getWorkspaceRoute } = useAuth();
  const { theme, setTheme, isManualPreference, clearPreference, checkTimeBasedPrompt } = useTheme();
  const navigate = useNavigate();
  
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [passwordChanged, setPasswordChanged] = useState(false);

  // Personal PIN state
  const [pinStatus, setPinStatus] = useState(null); // { has_pin }
  const [pinLoading, setPinLoading] = useState(true);
  const [pinForm, setPinForm] = useState({ pin: '', confirm_pin: '', current_password: '' });
  const [pinSaving, setPinSaving] = useState(false);
  const [pinError, setPinError] = useState('');
  const [pinSuccess, setPinSuccess] = useState(false);
  const [showPinForm, setShowPinForm] = useState(false);

  // Self-recovery state
  const [showRecovery, setShowRecovery] = useState(false);
  const [recoveryStep, setRecoveryStep] = useState('password'); // 'password' | 'code' | 'new_pin'
  const [recoveryPassword, setRecoveryPassword] = useState('');
  const [recoveryCode, setRecoveryCode] = useState('');
  const [recoveryPin, setRecoveryPin] = useState('');
  const [recoveryConfirm, setRecoveryConfirm] = useState('');
  const [recoveryEmail, setRecoveryEmail] = useState('');
  const [recoveryLoading, setRecoveryLoading] = useState(false);
  const [recoveryError, setRecoveryError] = useState('');

  const API = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem('token');

  useEffect(() => {
    // Check for time-based dark mode prompt
    checkTimeBasedPrompt();
  }, [checkTimeBasedPrompt]);

  // Fetch PIN status on mount
  useEffect(() => {
    if (user?.role === 'super_admin') {
      fetch(`${API}/api/auth/personal-pin/status`, {
        headers: { 'Authorization': `Bearer ${token}` },
      })
        .then(r => r.ok ? r.json() : null)
        .then(d => { if (d) setPinStatus(d); })
        .catch(() => {})
        .finally(() => setPinLoading(false));
    } else {
      setPinLoading(false);
    }
  }, [user, API, token]);

  const T = (en, fr, es) => {
    const l = i18n.language;
    if (l === 'fr') return fr || en;
    if (l === 'es') return es || en;
    return en;
  };

  const handlePinSave = async () => {
    setPinError('');
    const { pin, confirm_pin, current_password } = pinForm;
    if (!/^\d{4}$/.test(pin)) {
      setPinError(T('PIN must be exactly 4 digits', 'Le NIP doit être exactement 4 chiffres', 'El PIN debe ser exactamente 4 dígitos'));
      return;
    }
    if (pin !== confirm_pin) {
      setPinError(T('PINs do not match', 'Les NIP ne correspondent pas', 'Los PIN no coinciden'));
      return;
    }
    setPinSaving(true);
    try {
      const res = await fetch(`${API}/api/auth/personal-pin`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ pin, confirm_pin, current_password }),
      });
      const data = await res.json();
      if (!res.ok) {
        setPinError(data.detail || T('Error setting PIN', 'Erreur lors de la configuration du NIP', 'Error al configurar el PIN'));
        return;
      }
      setPinStatus({ has_pin: true });
      setPinSuccess(true);
      setPinForm({ pin: '', confirm_pin: '', current_password: '' });
      setShowPinForm(false);
      toast.success(T('Personal PIN set successfully', 'NIP personnel configuré avec succès', 'PIN personal configurado exitosamente'));
      setTimeout(() => setPinSuccess(false), 3000);
    } catch {
      setPinError(T('Connection error', 'Erreur de connexion', 'Error de conexión'));
    } finally {
      setPinSaving(false);
    }
  };

  const startRecovery = () => {
    setShowRecovery(true);
    setShowPinForm(false);
    setRecoveryStep('password');
    setRecoveryPassword('');
    setRecoveryCode('');
    setRecoveryPin('');
    setRecoveryConfirm('');
    setRecoveryError('');
  };

  const handleRecoveryRequestCode = async () => {
    setRecoveryError('');
    if (!recoveryPassword) {
      setRecoveryError(T('Password is required', 'Le mot de passe est requis', 'La contraseña es requerida'));
      return;
    }
    setRecoveryLoading(true);
    try {
      const res = await fetch(`${API}/api/auth/pin-reset/request-code`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: recoveryPassword }),
      });
      const data = await res.json();
      if (!res.ok) {
        setRecoveryError(data.detail || T('Error', 'Erreur', 'Error'));
        return;
      }
      setRecoveryEmail(data.email_masked || '');
      setRecoveryStep('code');
      toast.success(T('Verification code sent to your email', 'Code de vérification envoyé à votre email', 'Código de verificación enviado a su email'));
    } catch {
      setRecoveryError(T('Connection error', 'Erreur de connexion', 'Error de conexión'));
    } finally {
      setRecoveryLoading(false);
    }
  };

  const handleRecoveryVerify = async () => {
    setRecoveryError('');
    if (recoveryCode.length !== 6) {
      setRecoveryError(T('Enter the 6-digit code', 'Entrez le code à 6 chiffres', 'Ingrese el código de 6 dígitos'));
      return;
    }
    if (!/^\d{4}$/.test(recoveryPin)) {
      setRecoveryError(T('New PIN must be 4 digits', 'Le nouveau NIP doit être 4 chiffres', 'El nuevo PIN debe ser 4 dígitos'));
      return;
    }
    if (recoveryPin !== recoveryConfirm) {
      setRecoveryError(T('PINs do not match', 'Les NIP ne correspondent pas', 'Los PIN no coinciden'));
      return;
    }
    setRecoveryLoading(true);
    try {
      const res = await fetch(`${API}/api/auth/pin-reset/verify`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: recoveryCode, new_pin: recoveryPin, confirm_pin: recoveryConfirm }),
      });
      const data = await res.json();
      if (!res.ok) {
        setRecoveryError(data.detail || T('Error', 'Erreur', 'Error'));
        return;
      }
      setPinStatus({ has_pin: true });
      setShowRecovery(false);
      toast.success(T('PIN has been reset successfully', 'Le NIP a été réinitialisé avec succès', 'El PIN se ha restablecido exitosamente'));
    } catch {
      setRecoveryError(T('Connection error', 'Erreur de connexion', 'Error de conexión'));
    } finally {
      setRecoveryLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    // Validation
    if (newPassword !== confirmPassword) {
      toast.error(i18n.language === 'fr' 
        ? 'Les mots de passe ne correspondent pas' 
        : 'Passwords do not match');
      return;
    }
    
    if (newPassword.length < 8) {
      toast.error(i18n.language === 'fr' 
        ? 'Le mot de passe doit contenir au moins 8 caractères' 
        : 'Password must be at least 8 characters');
      return;
    }
    
    setIsLoading(true);
    
    try {
      await authAPI.changePassword(currentPassword, newPassword);
      setPasswordChanged(true);
      toast.success(i18n.language === 'fr' 
        ? 'Mot de passe modifié. Veuillez vous reconnecter.' 
        : 'Password changed. Please login again.');
      
      // Logout after 2 seconds
      setTimeout(() => {
        logout();
        navigate('/login');
      }, 2000);
      
    } catch (error) {
      const message = error.response?.data?.detail || 
        (i18n.language === 'fr' ? 'Erreur lors du changement de mot de passe' : 'Error changing password');
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  if (passwordChanged) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="bg-card border-border w-full max-w-md">
          <CardContent className="pt-6">
            <div className="text-center space-y-4">
              <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto">
                <CheckCircle2 className="w-8 h-8 text-green-500" />
              </div>
              <h2 className="text-xl font-semibold text-foreground">
                {i18n.language === 'fr' ? 'Mot de passe modifié!' : 'Password Changed!'}
              </h2>
              <p className="text-muted-foreground">
                {i18n.language === 'fr' 
                  ? 'Redirection vers la page de connexion...' 
                  : 'Redirecting to login page...'}
              </p>
              <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Command Bar */}
      <CommandBar
        title={i18n.language === 'fr' ? 'Paramètres du compte' : 'Account Settings'}
        subtitle={user?.email}
      />

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-6 py-8 space-y-8">
        {/* Account Info Card */}
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-foreground flex items-center space-x-2">
              <User className="w-5 h-5" />
              <span>{i18n.language === 'fr' ? 'Informations du compte' : 'Account Information'}</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-muted-foreground">
                  {i18n.language === 'fr' ? 'Nom' : 'Name'}
                </Label>
                <p className="text-foreground font-medium">{user?.name}</p>
              </div>
              <div>
                <Label className="text-muted-foreground">
                  {i18n.language === 'fr' ? 'Email' : 'Email'}
                </Label>
                <p className="text-foreground font-medium">{user?.email}</p>
              </div>
              <div>
                <Label className="text-muted-foreground">
                  {i18n.language === 'fr' ? 'Rôle' : 'Role'}
                </Label>
                <p className="text-foreground font-medium capitalize">{user?.role}</p>
              </div>
              <div>
                <Label className="text-muted-foreground">
                  {i18n.language === 'fr' ? 'Secteur' : 'Industry'}
                </Label>
                <p className="text-foreground font-medium capitalize">{user?.industry_type}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Appearance Settings Card */}
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-foreground flex items-center space-x-2">
              <Palette className="w-5 h-5" />
              <span>{i18n.language === 'fr' ? 'Apparence' : 'Appearance'}</span>
            </CardTitle>
            <CardDescription className="text-muted-foreground">
              {i18n.language === 'fr' 
                ? 'Personnalisez l\'apparence de l\'interface'
                : 'Customize the look and feel of the interface'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-foreground font-medium">
                    {i18n.language === 'fr' ? 'Thème' : 'Theme'}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {i18n.language === 'fr' 
                      ? isManualPreference 
                        ? 'Préférence manuelle active' 
                        : 'Utilise les paramètres par défaut'
                      : isManualPreference 
                        ? 'Manual preference active' 
                        : 'Using default settings'}
                  </p>
                </div>
                <div className="flex items-center space-x-2">
                  <Button
                    variant={theme === 'light' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setTheme('light')}
                    data-testid="theme-light-btn"
                    className={theme === 'light' ? 'bg-primary text-primary-foreground' : ''}
                  >
                    <Sun className="w-4 h-4 mr-2" />
                    {i18n.language === 'fr' ? 'Clair' : 'Light'}
                  </Button>
                  <Button
                    variant={theme === 'dark' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setTheme('dark')}
                    data-testid="theme-dark-btn"
                    className={theme === 'dark' ? 'bg-primary text-primary-foreground' : ''}
                  >
                    <Moon className="w-4 h-4 mr-2" />
                    {i18n.language === 'fr' ? 'Sombre' : 'Dark'}
                  </Button>
                </div>
              </div>
              
              {isManualPreference && (
                <div className="pt-2 border-t border-border">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearPreference}
                    className="text-muted-foreground hover:text-foreground"
                  >
                    {i18n.language === 'fr' 
                      ? 'Réinitialiser aux paramètres par défaut' 
                      : 'Reset to default settings'}
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Personal PIN Card — Super Admins only */}
        {user?.role === 'super_admin' && (
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="text-foreground flex items-center space-x-2">
                <KeyRound className="w-5 h-5" />
                <span>{T('Personal Keypad PIN', 'NIP personnel du clavier', 'PIN personal del teclado')}</span>
              </CardTitle>
              <CardDescription className="text-muted-foreground">
                {T(
                  'Your personal 4-digit PIN works as an override on all store keypads.',
                  'Votre NIP personnel à 4 chiffres fonctionne comme code de remplacement sur tous les claviers de magasin.',
                  'Su PIN personal de 4 dígitos funciona como código de anulación en todos los teclados de tienda.'
                )}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {pinLoading ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Status display */}
                  <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <div className={`w-3 h-3 rounded-full ${pinStatus?.has_pin ? 'bg-green-500' : 'bg-yellow-500'}`} />
                      <div>
                        <p className="text-sm font-medium text-foreground">
                          {pinStatus?.has_pin
                            ? T('PIN is set', 'NIP configuré', 'PIN configurado')
                            : T('No PIN set', 'Aucun NIP configuré', 'No hay PIN configurado')}
                        </p>
                        {pinStatus?.has_pin && (
                          <p className="text-xs text-muted-foreground font-mono">••••</p>
                        )}
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => { setShowPinForm(!showPinForm); setPinError(''); setPinForm({ pin: '', confirm_pin: '', current_password: '' }); }}
                      data-testid="toggle-pin-form-btn"
                    >
                      {showPinForm
                        ? T('Cancel', 'Annuler', 'Cancelar')
                        : pinStatus?.has_pin
                          ? T('Change PIN', 'Changer le NIP', 'Cambiar PIN')
                          : T('Set PIN', 'Configurer le NIP', 'Configurar PIN')}
                    </Button>
                  </div>

                  {/* PIN form */}
                  {showPinForm && (
                    <div className="space-y-3 p-4 border border-border rounded-lg">
                      <div className="space-y-2">
                        <Label className="text-foreground">
                          {T('New PIN (4 digits)', 'Nouveau NIP (4 chiffres)', 'Nuevo PIN (4 dígitos)')}
                        </Label>
                        <Input
                          type="password"
                          inputMode="numeric"
                          maxLength={4}
                          value={pinForm.pin}
                          onChange={e => setPinForm({ ...pinForm, pin: e.target.value.replace(/\D/g, '').slice(0, 4) })}
                          placeholder="••••"
                          className="bg-background border-input font-mono text-center text-lg tracking-[0.5em]"
                          data-testid="personal-pin-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-foreground">
                          {T('Confirm PIN', 'Confirmer le NIP', 'Confirmar PIN')}
                        </Label>
                        <Input
                          type="password"
                          inputMode="numeric"
                          maxLength={4}
                          value={pinForm.confirm_pin}
                          onChange={e => setPinForm({ ...pinForm, confirm_pin: e.target.value.replace(/\D/g, '').slice(0, 4) })}
                          placeholder="••••"
                          className="bg-background border-input font-mono text-center text-lg tracking-[0.5em]"
                          data-testid="personal-pin-confirm-input"
                        />
                      </div>
                      {pinStatus?.has_pin && (
                        <div className="space-y-2">
                          <Label className="text-foreground">
                            {T('Current password (to confirm)', 'Mot de passe actuel (pour confirmer)', 'Contraseña actual (para confirmar)')}
                          </Label>
                          <Input
                            type="password"
                            value={pinForm.current_password}
                            onChange={e => setPinForm({ ...pinForm, current_password: e.target.value })}
                            className="bg-background border-input"
                            data-testid="pin-current-password-input"
                          />
                        </div>
                      )}

                      {pinError && (
                        <p className="text-sm text-destructive" data-testid="pin-error">{pinError}</p>
                      )}

                      <div className="flex items-start space-x-3 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                        <Shield className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                        <p className="text-xs text-blue-500">
                          {T(
                            'Your PIN is stored securely (hashed). If you forget it, you can reset it from this page using your password and email.',
                            'Votre NIP est stocké de manière sécurisée (haché). Si vous l\'oubliez, vous pouvez le réinitialiser depuis cette page avec votre mot de passe et email.',
                            'Su PIN se almacena de forma segura (hash). Si lo olvida, puede restablecerlo desde esta página con su contraseña y email.'
                          )}
                        </p>
                      </div>

                      <Button
                        onClick={handlePinSave}
                        disabled={pinSaving || pinForm.pin.length !== 4}
                        className="w-full bg-primary hover:bg-primary/90 text-primary-foreground"
                        data-testid="save-pin-btn"
                      >
                        {pinSaving ? (
                          <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{T('Saving...', 'Enregistrement...', 'Guardando...')}</>
                        ) : (
                          <><KeyRound className="w-4 h-4 mr-2" />{T('Save PIN', 'Enregistrer le NIP', 'Guardar PIN')}</>
                        )}
                      </Button>
                    </div>
                  )}

                  {pinSuccess && (
                    <div className="flex items-center space-x-2 p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                      <p className="text-sm text-green-500 font-medium">
                        {T('PIN saved', 'NIP enregistré', 'PIN guardado')}
                      </p>
                    </div>
                  )}

                  {/* Forgot PIN / Self-Recovery */}
                  {pinStatus?.has_pin && !showPinForm && !showRecovery && (
                    <button
                      onClick={startRecovery}
                      className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                      data-testid="forgot-pin-link"
                    >
                      {T('Forgot your PIN?', 'NIP oublié?', '¿Olvidó su PIN?')}
                    </button>
                  )}

                  {showRecovery && (
                    <div className="space-y-3 p-4 border border-amber-500/30 bg-amber-500/5 rounded-lg" data-testid="pin-recovery-panel">
                      <p className="text-sm font-medium text-foreground">
                        {T('Reset PIN via Email', 'Réinitialiser le NIP par email', 'Restablecer PIN por email')}
                      </p>

                      {recoveryStep === 'password' && (
                        <div className="space-y-3">
                          <div className="space-y-2">
                            <Label className="text-foreground text-sm">
                              {T('Confirm your account password', 'Confirmez votre mot de passe', 'Confirme su contraseña')}
                            </Label>
                            <Input
                              type="password"
                              value={recoveryPassword}
                              onChange={e => setRecoveryPassword(e.target.value)}
                              className="bg-background border-input"
                              data-testid="recovery-password-input"
                            />
                          </div>
                          {recoveryError && <p className="text-sm text-destructive" data-testid="recovery-error">{recoveryError}</p>}
                          <div className="flex gap-2">
                            <Button variant="outline" size="sm" onClick={() => setShowRecovery(false)} data-testid="recovery-cancel-btn">
                              {T('Cancel', 'Annuler', 'Cancelar')}
                            </Button>
                            <Button size="sm" onClick={handleRecoveryRequestCode} disabled={recoveryLoading} className="bg-primary" data-testid="recovery-send-code-btn">
                              {recoveryLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                              {T('Send code to my email', 'Envoyer le code à mon email', 'Enviar código a mi email')}
                            </Button>
                          </div>
                        </div>
                      )}

                      {recoveryStep === 'code' && (
                        <div className="space-y-3">
                          <p className="text-xs text-muted-foreground">
                            {T(`Code sent to ${recoveryEmail}`, `Code envoyé à ${recoveryEmail}`, `Código enviado a ${recoveryEmail}`)}
                          </p>
                          <div className="space-y-2">
                            <Label className="text-foreground text-sm">
                              {T('6-digit verification code', 'Code de vérification à 6 chiffres', 'Código de verificación de 6 dígitos')}
                            </Label>
                            <Input
                              inputMode="numeric"
                              maxLength={6}
                              value={recoveryCode}
                              onChange={e => setRecoveryCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                              placeholder="000000"
                              className="bg-background border-input font-mono text-center text-lg tracking-[0.3em]"
                              data-testid="recovery-code-input"
                            />
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <div className="space-y-1">
                              <Label className="text-foreground text-xs">{T('New PIN', 'Nouveau NIP', 'Nuevo PIN')}</Label>
                              <Input
                                type="password" inputMode="numeric" maxLength={4}
                                value={recoveryPin}
                                onChange={e => setRecoveryPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                                placeholder="••••"
                                className="bg-background border-input font-mono text-center"
                                data-testid="recovery-new-pin-input"
                              />
                            </div>
                            <div className="space-y-1">
                              <Label className="text-foreground text-xs">{T('Confirm', 'Confirmer', 'Confirmar')}</Label>
                              <Input
                                type="password" inputMode="numeric" maxLength={4}
                                value={recoveryConfirm}
                                onChange={e => setRecoveryConfirm(e.target.value.replace(/\D/g, '').slice(0, 4))}
                                placeholder="••••"
                                className="bg-background border-input font-mono text-center"
                                data-testid="recovery-confirm-pin-input"
                              />
                            </div>
                          </div>
                          {recoveryError && <p className="text-sm text-destructive" data-testid="recovery-error">{recoveryError}</p>}
                          <div className="flex gap-2">
                            <Button variant="outline" size="sm" onClick={() => setShowRecovery(false)} data-testid="recovery-cancel-btn">
                              {T('Cancel', 'Annuler', 'Cancelar')}
                            </Button>
                            <Button size="sm" onClick={handleRecoveryVerify} disabled={recoveryLoading} className="bg-primary" data-testid="recovery-verify-btn">
                              {recoveryLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                              {T('Reset PIN', 'Réinitialiser le NIP', 'Restablecer PIN')}
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Change Password Card */}
        <Card className="bg-card border-border">
          <CardHeader>
            <CardTitle className="text-foreground flex items-center space-x-2">
              <Lock className="w-5 h-5" />
              <span>{i18n.language === 'fr' ? 'Changer le mot de passe' : 'Change Password'}</span>
            </CardTitle>
            <CardDescription className="text-muted-foreground">
              {i18n.language === 'fr' 
                ? 'Après avoir changé votre mot de passe, vous devrez vous reconnecter sur tous vos appareils.'
                : 'After changing your password, you will need to login again on all your devices.'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handlePasswordChange} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="currentPassword" className="text-foreground">
                  {i18n.language === 'fr' ? 'Mot de passe actuel' : 'Current Password'}
                </Label>
                <Input
                  id="currentPassword"
                  data-testid="current-password-input"
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  required
                  className="bg-background border-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="newPassword" className="text-foreground">
                  {i18n.language === 'fr' ? 'Nouveau mot de passe' : 'New Password'}
                </Label>
                <Input
                  id="newPassword"
                  data-testid="new-password-input"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  minLength={8}
                  className="bg-background border-input"
                />
                <p className="text-xs text-muted-foreground">
                  {i18n.language === 'fr' ? 'Minimum 8 caractères' : 'Minimum 8 characters'}
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-foreground">
                  {i18n.language === 'fr' ? 'Confirmer le mot de passe' : 'Confirm Password'}
                </Label>
                <Input
                  id="confirmPassword"
                  data-testid="confirm-password-input"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  className="bg-background border-input"
                />
              </div>
              
              {/* Security Notice */}
              <div className="flex items-start space-x-3 p-3 bg-[hsl(var(--warning))]/10 border border-[hsl(var(--warning))]/30 rounded-lg">
                <Shield className="w-5 h-5 text-[hsl(var(--warning))] flex-shrink-0 mt-0.5" />
                <p className="text-sm text-[hsl(var(--warning))]">
                  {i18n.language === 'fr' 
                    ? 'Changer votre mot de passe déconnectera tous les appareils actuellement connectés.'
                    : 'Changing your password will sign out all currently logged-in devices.'}
                </p>
              </div>
              
              <Button
                type="submit"
                data-testid="change-password-btn"
                className="bg-primary hover:bg-primary/90 text-primary-foreground"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {i18n.language === 'fr' ? 'Modification...' : 'Changing...'}
                  </>
                ) : (
                  <>
                    <Lock className="w-4 h-4 mr-2" />
                    {i18n.language === 'fr' ? 'Changer le mot de passe' : 'Change Password'}
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
